/**
 * App-wide error boundary.
 *
 * Milestone 9 will wire the ``ErrorReporter`` (Sentry-ready) here. Milestone
 * 1 only catches, logs, and shows a graceful fallback so a rendering error
 * never leaves the user staring at a blank page.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";

interface State {
  error: Error | null;
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("[AppErrorBoundary]", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div
          data-testid="app-error-boundary"
          className="flex min-h-screen items-center justify-center bg-background p-8 text-foreground"
        >
          <div className="max-w-md space-y-4 rounded-lg border border-border bg-card p-8 shadow-sm">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              Something broke
            </p>
            <h1 className="font-display text-2xl font-semibold">
              The interface hit an unexpected error.
            </h1>
            <p className="text-sm text-muted-foreground">
              The error was captured. Reload the page to try again — the
              backend and your data are unaffected.
            </p>
            <pre className="max-h-40 overflow-auto rounded bg-secondary p-3 text-xs text-secondary-foreground">
              {this.state.error.message}
            </pre>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
